import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Container, Row, Col } from 'reactstrap';
import ReactTable from 'react-table';
import { FormattedMessage, injectIntl } from 'react-intl';
import debounce from 'lodash.debounce';
import { CustomButton } from '../../../containers';
import { columns } from './tableColumns';
import {
  redirectToAdminCreateEmployeePage,
  redirectToAdminEditEmployeePage,
} from '../../../redux/actions/navigationActions';
import { Query } from 'react-apollo';
import { get } from 'lodash-es';
import { handleRequestError } from '../../../constants/errorCodes';
import { GetUsersQuery } from '../../../operations/queries/GetUsersQuery';
import {
  convertKeyValueArrayToObject,
  haveArraysDiverged,
  convertIdValueArrayToObject,
} from '../../../utils/arrayOperations';
import {
  getSessionStorageEmployeesFilter,
  setSessionStorageEmployeesFilter,
} from '../../../constants/LocalStorageKeys';

export const AdminEmployeesListPage = injectIntl(props => {
  const dispatch = useDispatch();
  const DEFAULT_PAGE_SIZE = 14;
  const IS_EMPLOYEE = true;
  // let pageSize = DEFAULT_PAGE_SIZE;
  let reactTableFiltered = null;

  const storedQueryVariables = getSessionStorageEmployeesFilter();

  const [pageSize, setPageSize] = useState(get(storedQueryVariables, 'take', DEFAULT_PAGE_SIZE));
  const [page, setPage] = useState(get(storedQueryVariables, 'skip', 0) / pageSize);

  const defaultQueryVariables = { filter: { isEmployee: IS_EMPLOYEE }, skip: 0, take: DEFAULT_PAGE_SIZE };

  const variables = storedQueryVariables || defaultQueryVariables;
  const rawTotalAmount = get(variables, 'filter.totalAmount', '').replace(/\./g, '');
  // const rawTotalAmountEuro = get(variables, 'filter.totalAmountEuro', '').replace(/\./g, '');

  return (
    <Query
      query={GetUsersQuery}
      variables={{
        ...variables,
        filter: {
          ...variables.filter,
          ...(rawTotalAmount && {
            totalAmount: `${rawTotalAmount.replace(',', '.') * 100}`,
            // totalAmountEuro: `${rawTotalAmountEuro.replace(',', '.') * 100}`,
          }),
        },
      }}
      onError={error => handleRequestError(error, dispatch)}
      fetchPolicy="network-only"
      notifyOnNetworkStatusChange={true}
    >
      {({ loading, data, refetch }) => {
        const users = get(data, 'getUsers.users', []);
        const userCount = get(data, 'getUsers.queryCount', 0);

        const debouncedGetUsers = debounce(refetch, 400);

        const onFetchData = state => {
          if (data) {
            const filtersObject = convertIdValueArrayToObject(state.filtered);
            const order = convertKeyValueArrayToObject(
              state.sorted.map(filter => ({ [filter.id]: filter.desc ? 'DESC' : 'ASC' }))
            );

            if (filtersObject.hasOwnProperty('firstName')) {
              filtersObject.opBusinessFirstNameOrFirstNameLike = filtersObject.firstName;
              delete filtersObject.firstName;
            }
            if (filtersObject.hasOwnProperty('lastName')) {
              filtersObject.opBusinessLastNameOrLastNameOr = filtersObject.lastName;
              delete filtersObject.lastName;
            }

            const queryVariables = {
              filter: {
                ...filtersObject,
                isEmployee: IS_EMPLOYEE,
              },
              order,
              skip: state.page * state.pageSize,
              take: state.pageSize,
            };

            setSessionStorageEmployeesFilter(queryVariables);

            if (
              !reactTableFiltered ||
              !haveArraysDiverged(state.filtered, reactTableFiltered, [
                'partnerId',
                'email',
                'phoneNumber',
                'firstName',
                'lastName',
                'oib',
              ])
            ) {
              reactTableFiltered = state.filtered;
              debouncedGetUsers.cancel();

              refetch({
                ...queryVariables,
                filter: {
                  ...queryVariables.filter,
                  ...(rawTotalAmount && {
                    totalAmount: `${rawTotalAmount.replace(',', '.') * 100}`,
                    // totalAmountEuro: `${rawTotalAmountEuro.replace(',', '.') * 100}`,
                  }),
                },
              });
            } else {
              debouncedGetUsers({
                ...queryVariables,
                filter: {
                  ...queryVariables.filter,
                  ...(rawTotalAmount && {
                    totalAmount: `${rawTotalAmount.replace(',', '.') * 100}`,
                    // totalAmountEuro: `${rawTotalAmountEuro.replace(',', '.') * 100}`,
                  }),
                },
              });
            }
          }
        };

        return (
          <Container fluid>
            <Row>
              <Col xs="12">
                <Row className="mb-3">
                  <Col xs="12" sm="6" className="d-flex align-items-center mb-3 mb-sm-0">
                    <h3 className="mb-0">
                      <FormattedMessage id="adminEmployeeListPage.title" />
                    </h3>
                  </Col>
                  <Col xs="12" sm="6" className="text-left text-sm-right">
                    <CustomButton
                      className="hok-dashboard-btn ml-auto"
                      block={false}
                      translationId="createEmployee"
                      onClick={() => dispatch(redirectToAdminCreateEmployeePage())}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col xs="12">
                    <ReactTable
                      className="-striped -highlight"
                      style={{ cursor: 'pointer' }}
                      filterable
                      data={users}
                      columns={columns}
                      pageSizeOptions={[DEFAULT_PAGE_SIZE, 25, 50, 100]}
                      defaultPageSize={DEFAULT_PAGE_SIZE}
                      loading={loading}
                      manual
                      onFetchData={onFetchData}
                      defaultFiltered={
                        get(storedQueryVariables, 'filter', null)
                          ? Object.keys(storedQueryVariables.filter).map(key => {
                              let k = key;

                              if (k === 'opBusinessFirstNameOrFirstNameLike') {
                                k = 'firstName';
                              }

                              if (k === 'opBusinessLastNameOrLastNameOr') {
                                k = 'lastName';
                              }

                              return {
                                id: k,
                                value: storedQueryVariables.filter[key],
                              };
                            })
                          : []
                      }
                      defaultSorted={
                        get(storedQueryVariables, 'order', null)
                          ? Object.keys(storedQueryVariables.order).map(key => ({
                              id: key,
                              desc: storedQueryVariables.order[key] === 'DESC',
                            }))
                          : [
                              {
                                id: 'createdAt',
                                desc: true,
                              },
                            ]
                      }
                      pages={Math.ceil(userCount / pageSize)}
                      page={page}
                      onPageChange={pageIndex => setPage(pageIndex)}
                      onPageSizeChange={pageSize => {
                        setPageSize(pageSize);
                        setPage(0);
                      }}
                      onFilteredChange={() => {
                        setPage(0);
                      }}
                      getTrProps={(_, rowInfo) => ({
                        onClick: () => dispatch(redirectToAdminEditEmployeePage(rowInfo.original.id)),
                      })}
                      loadingText={<FormattedMessage id="adminUsersListPage.tableLoading" />}
                      noDataText={<FormattedMessage id="adminUsersListPage.tableNoData" />}
                      previousText={<FormattedMessage id="adminUsersListPage.tablePreviousText" />}
                      nextText={<FormattedMessage id="adminUsersListPage.tableNextText" />}
                      pageText={<FormattedMessage id="adminUsersListPage.tablePageText" />}
                      ofText={<FormattedMessage id="adminUsersListPage.tableOfText" />}
                      rowsText={props.intl.formatMessage({ id: 'adminUsersListPage.tableRowsText' })}
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
          </Container>
        );
      }}
    </Query>
  );
});
